import React, { useState } from "react";
import EntityTypeSelector from "./EntityTypeSelector";
import EntityFactInputLoader from "./EntityFactInputLoader";
import EntityImportModal from "../Import/EntityImportModal";
import { EntityKind, EntitySchema } from "../../Dashboard/types";
import { useNavigate } from "react-router-dom";

interface EntityCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  excludeTypes?: EntityKind[];
  preselectedType?: EntityKind;
  initialName?: string;
  preventNavigation?: boolean;
  contextEntity?: EntitySchema; // Current entity context (e.g., manager when creating vehicle)
  // Bulk entry belongs to the top-level Create menu. Filling in one field of
  // one record is never a spreadsheet import, so callers that open this from a
  // field's picker leave it off (#11662).
  showImportSpreadsheet?: boolean;
}

const EntityCreationModal: React.FC<EntityCreationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  excludeTypes,
  preselectedType,
  initialName,
  preventNavigation,
  contextEntity,
  showImportSpreadsheet = false,
}) => {
  const [selectedEntityType, setSelectedEntityType] =
    useState<EntityKind | null>(preselectedType || null);
  const [currentStep, setCurrentStep] = useState<
    "typeSelection" | "factInput" | "import"
  >(preselectedType ? "factInput" : "typeSelection");
  const navigate = useNavigate();

  const handleEntityTypeSelect = (entityType: EntityKind) => {
    setSelectedEntityType(entityType);
    if (entityType === "interaction") {
      // Navigate to create page
      navigate("/rms/interactions/create");

      // Call success callback and close modal
      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } else {
      setCurrentStep("factInput");
    }
  };

  const handleBack = () => {
    setCurrentStep("typeSelection");
  };

  const handleClose = () => {
    setSelectedEntityType(preselectedType || null);
    setCurrentStep(preselectedType ? "factInput" : "typeSelection");
    onClose();
  };

  const handleSuccess = () => {
    if (onSuccess) {
      onSuccess();
    }
    handleClose();
  };

  if (!isOpen) return null;

  if (currentStep === "typeSelection") {
    return (
      <EntityTypeSelector
        isOpen={isOpen}
        onClose={handleClose}
        onEntityTypeSelect={handleEntityTypeSelect}
        onImportSpreadsheet={
          showImportSpreadsheet ? () => setCurrentStep("import") : undefined
        }
        excludeTypes={excludeTypes}
      />
    );
  }

  if (currentStep === "import") {
    return (
      <EntityImportModal
        isOpen={isOpen}
        onClose={handleClose}
        onImported={onSuccess}
      />
    );
  }

  if (currentStep === "factInput" && selectedEntityType) {
    return (
      <EntityFactInputLoader
        isOpen={isOpen}
        onClose={handleClose}
        onBack={handleBack}
        // The arrow renders only when there is a real prior step to go
        // back to (#11426 component sweep, M1).
        showBack={!preselectedType}
        entityType={selectedEntityType}
        onSuccess={handleSuccess}
        initialName={initialName}
        preventNavigation={preventNavigation}
        contextEntity={contextEntity}
      />
    );
  }

  return null;
};

export default EntityCreationModal;
